import { Injectable } from '@angular/core';
import { Credentials } from '../models/credentials';
import { of, BehaviorSubject, Observable } from 'rxjs';
import { User } from '../models/user';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Settings } from '../models/settings';
import { administersInPortal, appsWithAccess, asList, orgsOverflow, parseOrgs, tcPending } from '../lf-claims';
import { readLfClaims, readLfEmail } from '../sso/lf-token';
import { ssoMayImpersonate } from '../impersonation-access';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly LOGIN_URL = `${environment.apiUrl}/account/login`;
  private readonly CHECK_LOGIN_URL = `${environment.apiUrl}/account/checklogin`;
  private readonly USER_SETTINGS_URL = `${environment.apiUrl}/account/settings`;
  private readonly UPDATE_ANF_EXTRA_URL = `${environment.apiUrl}/account/updateanfextra`;

  private currentUserSource: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;

  private userSettingsSource = new BehaviorSubject<Settings | null>(null);
  public userSettings$ = this.userSettingsSource.asObservable();

  /**
   * The HTTP status of the last failed `/account/settings` call (0 when the request never got an
   * answer), or null when the settings loaded. The app shows a message in place of its pages
   * while this is set, instead of a page with an empty menu.
   */
  private settingsErrorSource = new BehaviorSubject<number | null>(null);
  public settingsError$ = this.settingsErrorSource.asObservable();

  /** Marks the stored session as an SSO one, so a reload knows not to call /account/checklogin. */
  private static readonly SSO_STORAGE_KEY = 'lf_sso';
  private perms: string[] = [];
  /** The token's `lf_roles`, the group-wide admin roles; empty for legacy sessions. */
  private roles: string[] = [];
  /** The roles on the token's `orgs` entries (`pharmacy_admin` among them), for "Aplicações". */
  private orgRoles: string[] = [];
  /** The token gives a role that allows "Ver como" (see `ssoMayImpersonate`). */
  private impersonationRole = false;
  /** The token's `orgs` was truncated (`orgs_overflow`), so `perms` may be missing grants. */
  private overflow = false;
  /** The token says the person's terms and conditions are pending (`tc_pending`). */
  private termsPendingClaim = false;

  constructor(private httpClient: HttpClient) {
    const currentUser = localStorage.getItem('currentUser');
    let parsedUser = null;
    if (currentUser) {
      parsedUser = JSON.parse(currentUser);
    }
    this.currentUserSource = new BehaviorSubject<User | null>(parsedUser);
    this.currentUser$ = this.currentUserSource.asObservable();
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'web-' + crypto.randomUUID();
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  public get currentUserValue(): User | null {
    return this.currentUserSource.value;
  }

  /** Permissions from a login-farmacias token; empty for legacy sessions. */
  public get permissions(): string[] {
    return this.perms;
  }

  /**
   * The token says the person's terms and conditions are pending. The entitlements API then withholds the other
   * company's permissions, so a person without `bidsweb:access` is refused for this reason; it only explains the
   * refusal and grants nothing. False for legacy sessions.
   */
  public get termsPending(): boolean {
    return this.termsPendingClaim;
  }

  /** The group-wide admin roles (`lf_roles`) from a login-farmacias token; empty for legacy sessions. */
  public get lfRoles(): string[] {
    return this.roles;
  }

  /**
   * Whether an SSO session may open the operational area: `bidsweb:operational` on any entry, or
   * the Administrador global or Bidscircle (login-farmacias grant-visibility G19), as the API's
   * operational policy reads it. The grant is not read per pharmacy: operational staff manage
   * every pharmacy's orders. `bidsops:access` is the Ops application and does not count.
   */
  public get ssoOperationalAccess(): boolean {
    return (
      this.perms.includes('bidsweb:operational') ||
      this.roles.includes('global_admin') ||
      this.roles.includes('company_admin:bidscircle')
    );
  }

  /** Whether the SSO token gives a role that allows "Ver como". False for legacy sessions, which have their own rule. */
  public get ssoCanImpersonate(): boolean {
    return this.impersonationRole;
  }

  /**
   * Where "Aplicações" goes: the portal's launcher, marked as opened from Bidscircle
   * (login-farmacias change `app-switcher`, D4 and D5). Null when there is nowhere to switch to: a
   * legacy session, a token granting this application only to someone who is not an administrator in the portal, or
   * no portal in this environment. A
   * truncated `orgs` shows the button, because this application holds no entitlements token to
   * ask for the full list; the launcher then shows what the person may open. Pending terms show it
   * too: the withheld permissions are not in the token, and the launcher is where the person accepts
   * the terms that release them. It grants nothing.
   */
  public get switcherUrl(): string | null {
    const portal = environment.sso?.portalUri;
    if (!environment.showAppSwitcher || !portal || !this.isSso) {
      return null;
    }
    const apps = appsWithAccess([{ perms: this.perms }]);
    const admin = administersInPortal(this.roles, [{ roles: this.orgRoles }]);
    return apps.length >= 2 || this.overflow || admin || this.termsPendingClaim ?`${portal}/apps?lf_switch=1&from=bidsweb` : null;
  }

  /**
   * Whether this session came from login-farmacias. Read from sessionStorage as well as the
   * field because it has to survive a reload: the stored `currentUser` looks identical either
   * way, and treating an SSO session as a legacy one would send it to /account/checklogin with
   * a refresh token it never had. MSAL's own cache is sessionStorage too, so the two live and
   * die together.
   */
  public get isSso(): boolean {
    return sessionStorage.getItem(AuthService.SSO_STORAGE_KEY) === '1';
  }

  /**
   * Adopt an SSO access token: the legacy `User` shape is populated from the token's claims so
   * every existing consumer keeps working unchanged, and the token's permissions are kept for
   * the guards. Roles and permissions come from the token, never from the DB.
   */
  public adoptSsoToken(accessToken: string): void {
    const claims = readLfClaims(accessToken);
    const orgs = parseOrgs(claims.orgs);
    // The pharmacy org is the one that carries an ANF; a lab user has none.
    const first = orgs.find((org) => org.anf) ?? orgs[0];

    this.perms = orgs.flatMap((org) => org.perms);
    this.orgRoles = orgs.flatMap((org) => org.roles);
    this.roles = asList(claims.lf_roles);
    this.impersonationRole = ssoMayImpersonate(claims);
    this.overflow = orgsOverflow(claims);
    this.termsPendingClaim = tcPending(claims);
    sessionStorage.setItem(AuthService.SSO_STORAGE_KEY, '1');

    const user: User = {
      email: readLfEmail(accessToken),
      token: accessToken,
      codigoAnf: first?.anf ?? '',
      refreshToken: '',
      role: first?.roles.includes('lab')
        ? 'lab'
        : first?.roles.includes('dev')
          ? 'dev'
          : 'user',
    };
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSource.next(user);
  }

  public get userSettingsValue() {
    return this.userSettingsSource.value;
  }

  public login(credentials: Credentials): Observable<User> {
    return this.httpClient
      .post<User>(this.LOGIN_URL, {
        email: credentials.email,
        password: credentials.password,
        deviceId: this.getDeviceId(),
      })
      .pipe(
        switchMap((user) => {
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.currentUserSource.next(user);
          return this.getUserSettings().pipe(switchMap(() => of(user)));
        })
      );
  }

  /**
   * Ends the session held here. This is the teardown a guard or a 401 performs; it leaves the
   * group session alone, because one application refusing someone is not a reason to sign them
   * out of the others. A sign-out the person asked for goes through `MsalService.logout()`.
   */
  public logout(): void {
    localStorage.removeItem('currentUser');
    sessionStorage.removeItem(AuthService.SSO_STORAGE_KEY);
    this.perms = [];
    this.roles = [];
    this.orgRoles = [];
    this.impersonationRole = false;
    this.overflow = false;
    this.termsPendingClaim = false;
    this.currentUserSource.next(null);
    this.userSettingsSource.next(null);
    this.settingsErrorSource.next(null);
  }

  /**
   * Session restore at boot, once MSAL has run. It never fails, so the app always boots:
   * - a legacy session that `/account/checklogin` no longer accepts is dropped, and the guards
   *   then send the person to the login page;
   * - a settings call that fails leaves the session in place with `settingsError$` set, so the
   *   app can say what happened and offer a way out.
   */
  public restoreSession(): Observable<Settings | null> {
    if (!this.currentUserValue) {
      return of(null);
    }

    return this.checkLogin().pipe(
      catchError(() => {
        this.logout();
        return of(null);
      }),
      switchMap((user) => (user ? this.getUserSettings() : of(null))),
      catchError(() => of(null))
    );
  }

  public checkLogin() {
    // An SSO session refreshes through MSAL, which the interceptor asks on every request. There
    // is no refresh token to spend here and /account/checklogin would reject it.
    const current = this.currentUserValue;
    if (this.isSso && current) {
      this.restorePermissionsFromStoredToken(current);
      return of(current);
    }

    return this.httpClient
      .post<User>(this.CHECK_LOGIN_URL, {
        email: this.currentUserValue?.email,
        refreshToken: this.currentUserValue?.refreshToken,
        deviceId: this.getDeviceId(),
      })
      .pipe(
        switchMap((user) => {
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.currentUserSource.next(user);
          return of(user);
        })
      );
  }

  /**
   * A reload has no token in memory, so the permissions are re-read from the stored one. The
   * MSAL bootstrap replaces them with a fresh token's perms a moment later; this keeps the
   * guards from refusing the first navigation in the meantime.
   */
  private restorePermissionsFromStoredToken(user: User): void {
    if (this.perms.length > 0 || this.roles.length > 0) {
      return;
    }

    try {
      const claims = readLfClaims(user.token);
      this.perms = parseOrgs(claims.orgs).flatMap((org) => org.perms);
      this.orgRoles = parseOrgs(claims.orgs).flatMap((org) => org.roles);
      this.roles = asList(claims.lf_roles);
      this.impersonationRole = ssoMayImpersonate(claims);
      this.overflow = orgsOverflow(claims);
      this.termsPendingClaim = tcPending(claims);
    } catch {
      this.perms = [];
      this.orgRoles = [];
      this.roles = [];
      this.impersonationRole = false;
      this.overflow = false;
      this.termsPendingClaim = false;
    }
  }

  public getUserSettings() {
    return this.httpClient
      .get<Settings>(this.USER_SETTINGS_URL)
      .pipe(
        tap({
          next: (settings) => {
            this.settingsErrorSource.next(null);
            this.userSettingsSource.next(settings);
          },
          error: (err) =>
            this.settingsErrorSource.next(err instanceof HttpErrorResponse ? err.status : 0),
        })
      );
  }

  public updateAnfExtra(editableAnfExtraCodes: number[]) {
    return this.httpClient
      .put<Settings>(this.UPDATE_ANF_EXTRA_URL, { editableAnfExtraCodes })
      .pipe(tap((settings) => this.userSettingsSource.next(settings)));
  }
}
